import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { onboardingKeys, type OnboardingStepKey } from '@/features/onboarding/api'
import { useApi } from '@/lib/api-context'
import { useUploader } from '@/lib/upload'
import type { DocType } from './doc-number'

/**
 * Empresa: o cadastro da clínica ativa (docs/api/admin.md, "Empresa"; RN-ADM-01 a 09). Os tipos do
 * cadastro servem também ao painel root (`features/clinics/api.ts`), que edita a mesma entidade.
 */

export type Audience = 'patient' | 'student' | 'employee' | 'third_party'
export type ClinicSignatureMode = 'manual' | 'automatic'
/** Onde o logo entra no documento (RN-ADM-05): no alto da primeira página ou ao fundo de todas. */
export type LogoPosition = 'header' | 'watermark'
export type LogoMimeType = 'image/png' | 'image/jpeg'

export interface ClinicAddress {
  postalCode: string
  street: string
  number: string
  complement: string | null
  district: string
  city: string
  state: string
}

/** Documento de pessoa (responsável legal, testemunha): sempre CPF (T22). */
export interface PersonDocument {
  name: string
  docType: 'cpf'
  docNumber: string
}

/** Pessoa que agiu; `{ id: null, name: 'Westhetics' }` é o autor sistema. */
export interface ActorRef {
  id: string | null
  name: string
}

/** O resumo da implantação dentro do objeto de clínica (RN-ADM-85). */
export interface ClinicOnboardingSummary {
  isCompleted: boolean
  completedAt: string | null
  doneStepCount: number
  stepCount: number
  currentStep: OnboardingStepKey | null
}

/** O que Empresa e o painel root têm em comum no objeto de clínica. */
export interface ClinicRecord {
  id: string
  legalName: string
  tradeName: string | null
  docType: DocType
  docNumber: string
  /** Nulo até a etapa Empresa da clínica de cadastro próprio (RN-ADM-89). */
  address: ClinicAddress | null
  phone: string | null
  legalEmail: string
  legalRepresentative: PersonDocument | null
  timezone: string
  logoPosition: LogoPosition
  witnesses: PersonDocument[]
  clinicSignatureMode: ClinicSignatureMode
  status: 'active' | 'blocked'
  audiences: Audience[]
  onboarding: ClinicOnboardingSummary
  createdAt: string
  updatedAt: string
  updatedBy: ActorRef
}

/** O logo salvo (RN-ADM-05). `url` é assinada e vale 15 minutos: serve para mostrar, não para guardar. */
export interface ClinicLogo {
  url: string
  width: number
  height: number
  mimeType: LogoMimeType
}

export interface ClinicProfile extends ClinicRecord {
  /**
   * A gestora pode corrigir o documento: só na clínica de cadastro próprio, até concluir a
   * implantação (RN-ADM-88). Nas demais, o documento é da Westhetics (RN-ADM-01).
   */
  isDocNumberEditable: boolean
  logo: ClinicLogo | null
  autoSignature: { enabledBy: ActorRef; enabledAt: string } | null
  network: { id: string; name: string; sharesPatients: boolean } | null
}

/**
 * Corpo de `PUT /admin/clinic-profile`: campo ausente não muda; o opcional enviado `null` é limpo.
 * O endereço é salvo inteiro. `logoId` confirma um envio feito por `PUT /admin/clinic-profile/logo`;
 * `null` remove o logo; ausente, o logo fica como está (RN-ADM-05).
 */
export interface ClinicProfileInput {
  /** Só com `isDocNumberEditable` (RN-ADM-88); para as demais clínicas a API o ignora. */
  docNumber?: string
  docType?: DocType
  legalName?: string
  tradeName?: string | null
  address?: ClinicAddress
  phone?: string | null
  legalEmail?: string
  legalRepresentative?: PersonDocument | null
  timezone?: string
  logoId?: string | null
  logoPosition?: LogoPosition
  witnesses?: PersonDocument[]
  clinicSignatureMode?: ClinicSignatureMode
}

/** O que `PUT /admin/clinic-profile/logo` pede: o tipo é o lido dos bytes, não o da extensão. */
export interface LogoUploadRequest {
  file: Blob
  fileName: string
  mimeType: LogoMimeType
}

/** A reserva de envio: para onde mandar o arquivo e o `logoId` que o salvar confirma. */
export interface LogoUploadTicket {
  logoId: string
  uploadUrl: string
  method: 'PUT'
  headers: Record<string, string>
  /** A reserva vale 15 minutos: depois disso, salvar pede o envio de novo. */
  expiresAt: string
}

/** `GET /admin/postal-code/:postalCode`. CEP geral de cidade pequena não tem rua nem bairro. */
export interface PostalCodeAddress {
  postalCode: string
  street: string | null
  district: string | null
  city: string
  state: string
}

export const clinicProfileKeys = { all: ['clinic-profile'] as const }

export function useClinicProfile(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: clinicProfileKeys.all,
    queryFn: () => api.get<ClinicProfile>('/admin/clinic-profile'),
    enabled: options.enabled ?? true,
  })
}

/** Salvar a empresa muda o cadastro e o resumo da implantação (testemunhas, assinatura da clínica). */
export function useSaveClinicProfile() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ClinicProfileInput) =>
      api.put<ClinicProfile>('/admin/clinic-profile', input),
    onSuccess: (profile) => {
      queryClient.setQueryData(clinicProfileKeys.all, profile)
      return queryClient.invalidateQueries({ queryKey: onboardingKeys.all })
    },
  })
}

/**
 * Envia o logo (RN-ADM-05): reserva o envio na API e manda o arquivo direto ao Storage, pela URL
 * assinada. O logo só passa a valer quando a empresa é salva com o `logoId` devolvido.
 */
export function useUploadLogo() {
  const api = useApi()
  const upload = useUploader()
  return useMutation({
    mutationFn: async ({ file, fileName, mimeType }: LogoUploadRequest) => {
      const ticket = await api.put<LogoUploadTicket>('/admin/clinic-profile/logo', {
        fileName,
        mimeType,
        sizeBytes: file.size,
      })
      await upload(ticket, file)
      return ticket
    },
  })
}

/** Consulta de CEP pela API (RN-ADM-03): só preenche; o endereço segue editável e nunca depende dela. */
export function useLookupPostalCode() {
  const api = useApi()
  return useMutation({
    mutationFn: (postalCode: string) =>
      api.get<PostalCodeAddress>(`/admin/postal-code/${postalCode}`),
  })
}

// Provedor de assinatura (docs/api/admin.md, "Provedor de assinatura"; RN-ASS-33, T49) ---------------------

/**
 * Quem conduz a assinatura dos envios da clínica. É sempre a Autentique, configurada pela Westhetics (T49): a
 * clínica só lê, e a tela de envio usa para dizer que quem manda o link é a Autentique.
 */
export type SignatureProvider = 'autentique' | 'westhetics'

/** O token da conta Autentique da clínica: entra e nunca sai; a tela só sabe se há, de quem e de quando. */
export interface AutentiqueTokenStatus {
  isConfigured: boolean
  configuredAt: string | null
  configuredBy: ActorRef | null
  accountEmail: string | null
  /** O token é da conta do e-mail jurídico: só assim a clínica assina sozinha na Autentique. */
  matchesLegalEmail: boolean
}

export interface SignatureProviderSettings {
  provider: SignatureProvider
  legalEmail: string
  /** A Autentique está configurada neste ambiente (`AUTENTIQUE_API_KEY`). */
  isAutentiqueAvailable: boolean
  /** A Autentique está em modo de teste (staging): os documentos não têm validade jurídica. */
  isSandbox: boolean
  autentiqueToken: AutentiqueTokenStatus
  /** Gestora ou root com clínica ativa. */
  canManage: boolean
}

export const signatureProviderKeys = { all: ['signature-provider'] as const }

export function useSignatureProvider(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: signatureProviderKeys.all,
    queryFn: () => api.get<SignatureProviderSettings>('/admin/signature-provider'),
    enabled: options.enabled ?? true,
  })
}
